/**
 * Centralized legal context per country for AI document generation and review.
 * Single source of truth - used by document-review, groq-client, contract-generation, legal-assistant.
 * @see docs/COUNTRY_LEGAL_DATA.md for validation and update instructions.
 */

export type CountryCode = "MX" | "RD" | "CO" | "CL" | "PE" | "US";

export interface CountryLegalContext {
  es: string;
  en: string;
}

/** Detailed legal context for AI prompts - laws must be verified and current */
export const COUNTRY_LEGAL_CONTEXT: Record<CountryCode, CountryLegalContext> = {
  MX: {
    es: "México: Marco jurídico federal y local. Considera el Código Civil Federal, Código de Comercio (reformas 2024), Ley Federal del Trabajo, Ley de Protección de Datos Personales (LFPDPPP) y NOMs aplicables según industria.",
    en: "Mexico: Federal and local legal framework. Consider the Federal Civil Code, Commercial Code (2024 reforms), Federal Labor Law, Personal Data Protection Law (LFPDPPP), and applicable NOMs according to industry.",
  },
  RD: {
    es: "República Dominicana: Sistema basado en derecho civil (Napoleónico). Considera el Código Civil, Ley No. 479-08 de Sociedades Comerciales, Ley 141-15 de Reestructuración Mercantil, y el Código de Trabajo (Ley 16-92). Art. 75-79 Código de Trabajo para causales de terminación.",
    en: "Dominican Republic: System based on civil law (Napoleonic). Consider the Civil Code, Law No. 479-08 on Commercial Companies, Law 141-15 on Commercial Restructuring, and the Labor Code (Law 16-92). Arts. 75-79 Labor Code for termination causes.",
  },
  CO: {
    es: "Colombia: Sistema civilista. Considera el Código Civil, Código de Comercio, Ley 1581 (Protección de datos), Ley 222 (Sociedades), Código Sustantivo del Trabajo y decretos reglamentarios recientes.",
    en: "Colombia: Civil law system. Consider the Civil Code, Commercial Code, Law 1581 (Data Protection), Law 222 (Companies), Substantive Labor Code, and recent regulatory decrees.",
  },
  CL: {
    es: "Chile: Derecho civil codificado. Considera el Código Civil, Código del Trabajo, Ley 18.046 sobre Sociedades Anónimas, Ley 19.496 (Protección al Consumidor) y Ley 19.628 (Protección de la Vida Privada).",
    en: "Chile: Codified civil law. Consider the Civil Code, Labor Code, Law 18.046 on Stock Corporations, Law 19.496 (Consumer Protection), and Law 19.628 (Privacy Protection).",
  },
  PE: {
    es: "Perú: Derecho romano-germánico. Considera el Código Civil (1984), Ley General de Sociedades 26887, Decreto Legislativo 728 (Ley de Productividad y Competitividad Laboral) y normativa INDECOPI.",
    en: "Peru: Roman-Germanic law. Consider the Civil Code (1984), General Companies Law 26887, Legislative Decree 728 (Labor Productivity and Competitiveness Law), and INDECOPI regulations.",
  },
  US: {
    es: "Estados Unidos: Common Law. Considera la Constitución federal, códigos por estado (U.S. Code Title 15 Commerce, Title 18 Penal, Title 28 Judiciary, Title 29 Labor) y regulaciones federales aplicables.",
    en: "United States: Common Law. Consider the federal Constitution, state-specific codes (U.S. Code Title 15 Commerce, Title 18 Crimes, Title 28 Judiciary, Title 29 Labor), and applicable federal regulations.",
  },
};

const DEFAULT_CONTEXT: CountryLegalContext = {
  es: "Marco legal latinoamericano basado en el sistema del Derecho Civil (Romano-Germánico).",
  en: "Latin American legal framework based on the Civil Law system (Roman-Germanic).",
};

/** Short context for document generation prompts (groq-client) */
export const COUNTRY_CONTEXT_FOR_DOC_GENERATION: Record<
  CountryCode,
  string
> = {
  MX: "México, basándote en el Código Civil Federal Mexicano",
  RD: "República Dominicana, basándote en el Código Civil Dominicano",
  CO: "Colombia, basándote en el Código Civil Colombiano",
  CL: "Chile, basándote en el Código Civil Chileno",
  PE: "Perú, basándote en el Código Civil Peruano",
  US: "Estados Unidos, basándote en el Common Law y U.S. Code (Títulos 15, 18, 28, 29)",
};

/** Context for legal assistant (es/en) */
export const COUNTRY_CONTEXT_FOR_ASSISTANT: Record<
  CountryCode,
  CountryLegalContext
> = {
  MX: {
    es: "México, basándote en el Código Civil Federal Mexicano, Constitución Política de los Estados Unidos Mexicanos y leyes federales vigentes",
    en: "Mexico, based on the Mexican Federal Civil Code, Political Constitution of the United Mexican States and current federal laws",
  },
  RD: {
    es: "República Dominicana, basándote en el Código Civil Dominicano, Constitución de la República Dominicana y leyes vigentes",
    en: "Dominican Republic, based on the Dominican Civil Code, Constitution of the Dominican Republic and current laws",
  },
  CO: {
    es: "Colombia, basándote en el Código Civil Colombiano, Constitución Política de Colombia y el marco legal vigente",
    en: "Colombia, based on the Colombian Civil Code, Political Constitution of Colombia and the current legal framework",
  },
  CL: {
    es: "Chile, basándote en el Código Civil Chileno, Constitución Política de la República de Chile y leyes vigentes",
    en: "Chile, based on the Chilean Civil Code, Political Constitution of the Republic of Chile and current laws",
  },
  PE: {
    es: "Perú, basándote en el Código Civil Peruano, Constitución Política del Perú y marco legal actual",
    en: "Peru, based on the Peruvian Civil Code, Political Constitution of Peru and current legal framework",
  },
  US: {
    es: "Estados Unidos, basándote en el Common Law, Constitución federal y U.S. Code aplicable",
    en: "United States, based on Common Law, federal Constitution and applicable U.S. Code",
  },
};

/**
 * Get legal context for AI prompts by country and language
 */
export function getCountryLegalContext(
  country: string,
  language: "en" | "es" = "es"
): string {
  const code = country.toUpperCase() as CountryCode;
  const ctx = COUNTRY_LEGAL_CONTEXT[code];
  const lang = language === "en" ? "en" : "es";
  return ctx?.[lang] ?? DEFAULT_CONTEXT[lang];
}

/** Get short context for document generation */
export function getCountryContextForDocGeneration(country: string): string {
  const code = country.toUpperCase() as CountryCode;
  return COUNTRY_CONTEXT_FOR_DOC_GENERATION[code] ?? "Latinoamérica";
}

/** Para getSystemPrompt en base-prompts (nombre, sistema legal, moneda) */
export const COUNTRY_CONTEXTS: Record<
  CountryCode,
  { name: string; legalSystem: string; key: string; currency: string }
> = {
  MX: {
    name: "México",
    legalSystem: "Derecho Civil (Sistema Neorromanista)",
    key: "Constitución Política, Código Civil Federal, Ley Federal del Trabajo",
    currency: "Pesos Mexicanos (MXN)",
  },
  RD: {
    name: "República Dominicana",
    legalSystem: "Derecho Civil (basado en el Código Napoleónico)",
    key: "Constitución de la República, Código Civil, Código de Trabajo",
    currency: "Pesos Dominicanos (DOP)",
  },
  CO: {
    name: "Colombia",
    legalSystem: "Derecho Civil",
    key: "Constitución Política, Código Civil, Código Sustantivo del Trabajo",
    currency: "Pesos Colombianos (COP)",
  },
  CL: {
    name: "Chile",
    legalSystem: "Derecho Civil",
    key: "Constitución Política, Código Civil, Código del Trabajo",
    currency: "Pesos Chilenos (CLP)",
  },
  PE: {
    name: "Perú",
    legalSystem: "Derecho Civil",
    key: "Constitución Política, Código Civil, Código de Trabajo",
    currency: "Soles (PEN)",
  },
  US: {
    name: "Estados Unidos",
    legalSystem: "Common Law",
    key: "Constitución, U.S. Code Title 15, 18, 28, 29",
    currency: "Dólares (USD)",
  },
};

export const DOMINICAN_REPUBLIC_CONFIG = {
  legalSystem: "Derecho Civil (basado en el Código Napoleónico)",
  constitution: "Constitución de la República Dominicana (2015)",
  currency: "Pesos Dominicanos (DOP)",
  jurisdiction: "República Dominicana",
  keyLaws: {
    civil: ["Código Civil de la República Dominicana"],
    labor: ["Código de Trabajo (Ley 16-92)"],
    commercial: ["Ley 479-08 de Sociedades Comerciales"],
    criminal: ["Código Procesal Penal (Ley 76-02)"],
  },
};

export function getDocumentConfigForRD(documentType: string) {
  const configs: Record<
    string,
    {
      requiredClauses: string[];
      legalReferences: string[];
      format: string;
    }
  > = {
    "contract-work": {
      requiredClauses: [
        "Identificación de las partes",
        "Objeto del contrato",
        "Salario",
        "Jornada",
        "Duración",
      ],
      legalReferences: ["Código de Trabajo de la RD (Ley 16-92)"],
      format: "Estándar legal dominicano",
    },
    "contract-rent": {
      requiredClauses: ["Partes", "Inmueble", "Precio", "Depósito", "Vigencia"],
      legalReferences: ["Código Civil Dominicano", "Ley 4314 sobre Alquileres"],
      format: "Estándar de arrendamiento RD",
    },
  };
  return configs[documentType] || null;
}

export function getCommonClausesForRD(types: string[]): string[] {
  const clauses: Record<string, string> = {
    jurisdiction:
      "Las partes se someten a la jurisdicción de los tribunales de la República Dominicana.",
    applicableLaw:
      "El presente contrato se rige por las leyes de la República Dominicana.",
    currency: "Todos los pagos se realizarán en Pesos Dominicanos (DOP).",
  };
  return types.map((t) => clauses[t]).filter(Boolean);
}

/** Get context for legal assistant by language */
export function getCountryContextForAssistant(
  country: string,
  language: "es" | "en"
): string {
  const code = country.toUpperCase() as CountryCode;
  const ctx = COUNTRY_CONTEXT_FOR_ASSISTANT[code] ?? COUNTRY_CONTEXT_FOR_ASSISTANT.MX;
  return ctx[language];
}
